import React, { useEffect, useState } from "react";
import {
  addIndex,
  allPass,
  always,
  complement,
  converge,
  curry,
  eqProps,
  equals,
  filter,
  groupWith,
  head,
  identity,
  isEmpty,
  isNil,
  last,
  length,
  lensProp,
  map,
  over,
  pipe,
  prepend,
  prop,
  propEq,
  tail,
  takeLast,
  unless,
  when,
} from "ramda";
import { faList, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";

import Avatar from "./Avatar/Avatar";
import CardResult from "../../Widgets/Carousels/CardResult";
import CarouselCards from "../../Widgets/Carousels/CarouselCards";
import ChristmasLights from "./ChristmasLights";
import Feedback from "./Feedback/Feedback";
import LoadingSprite from "./LoadingSprite/LoadingSprite";
import PoweredByHyro from "./PoweredByHyro/PoweredByHyro";
import Suggestion from "./Suggestions/Suggestion/Suggestion";
import Suggestions from "./Suggestions/Suggestions";
import TextWithTags from "../View/TextWithTags/TextWithTags";
import { createUseStyles } from "react-jss";
import { eventTypes } from "../../../lib/analytics";
import { makeStylesFromThemeAndCustomStyles } from "../../../lib/StyleFunctions";
import messageGroupStylesObject from "./MessageGroupStyles";
import preRecordedMessages from "../../../lib/audio/audio";

const LEFT_INDENT = "43px";

const useStylesMessageGroups = createUseStyles(() => ({
  lowerContentWrapper: { marginTop: "1px" },
  suggestionContainer: { marginLeft: LEFT_INDENT, width: "auto" },
  feedbackContainer: { marginLeft: LEFT_INDENT, marginTop: "8px" },
  ...messageGroupStylesObject,
}));

const useStylesMessage = (customWidgetStyle) =>
  createUseStyles(
    makeStylesFromThemeAndCustomStyles(
      (theme) => ({
        message: {
          width: "auto",
          color: "white",
          "& p": {
            "& + p": {
              marginTop: "12px",
            },
          },
          borderRadius: "7px",
          position: "relative",
          whiteSpace: "pre-line",
          marginTop: "10px",
          flexDirection: "column",
          padding: "12px 15px !important",
          WebkitTransition: "all 150ms ease",
          MozTransition: "background-color all 150ms ease",
          OTransition: "background-color all 150ms ease",
          transition: "background-color all 150ms ease",
          lineHeight: "20px",
          fontSize: "14px",
          fontWeight: "400",
          overflowWrap: "anywhere", // Break long URLs.
          wordBreak: "break-word",
        },
        userMessage: {
          composes: "$message",
          background: theme.secondaryColor,
          fontWeight: "400",
          flexDirection: "row",
          marginRight: "7px",
          marginLeft: "50px", // Align with left edge of bot message.
          "& a": {
            color: "white",
            textDecoration: "underline",
          },
        },
        lastUserMessage: {
          composes: "$lastMessage",
          marginBottom: "0",
          "&:after": {
            borderBottomColor: `${theme.secondaryColor} !important`,
            right: "-7px",
            left: "auto !important",
          },
        },
        lastAgentMessage: {
          composes: "$lastMessage",
          "&:after": {
            borderBottomColor: `${theme.agentColor} !important`,
          },
        },
        lastMessage: {
          marginBottom: "5px",
          "&:after": {
            border: "solid transparent",
            content: '" "',
            height: "0",
            width: "0",
            position: "absolute",
            pointerEvents: "none",
            borderWidth: "10px",
            marginLeft: "-10px",
            borderBottomColor: "white",
            left: "0px",
            bottom: "0px",
          },
        },
        botMessage: {
          composes: "$message",
          background: "white",
          color: theme.textColor,
          borderRadius: "8px",
          "& img": {
            maxWidth: "100% !important",
          },
        },
        agentMessage: {
          composes: "$botMessage",
          background: theme.agentColor,
        },
        imageSuggestions: {
          composes: "$botMessage",
          width: "85%",
        },
        avatarContainer: { margin: "0 8px" },
      }),
      customWidgetStyle
    )
  )();

const AgentMessageGroup = ({ messages }) => {
  const styles = useStylesMessageGroups();
  return (
    <div>
      <div
        className={styles.messageGroupContainer}
        style={{ display: "flex", alignItems: "flex-end" }}
      >
        <CustomAvatar
          customAvatar={
            '<svg width="19" height="27" viewBox="0 0 19 27" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9.31032 20.4828C14.4523 20.4828 18.6207 23.4006 18.6207 27H-2.28882e-05C-2.28882e-05 23.4006 4.16836 20.4828 9.31032 20.4828Z" fill="#464775"/><circle r="9.28205" transform="matrix(-1 0 0 1 9.28204 9.28205)" fill="#464775"/><circle cx="9.28201" cy="8.43826" r="1.68764" fill="#F8F9FF"/><circle cx="15.1887" cy="8.43826" r="1.68764" fill="#F8F9FF"/></svg>'
          }
        />
        <div className={styles.messageGroup}>
          {pipe(
            map((message) => message.text),
            addIndex(map)((text, index) => (
              <AgentUtterance
                key={index}
                text={text}
                isLastMessage={index === messages.length - 1}
              />
            ))
          )(messages)}
        </div>
      </div>
    </div>
  );
};

const AgentUtterance = ({ text, isLastMessage }) => {
  const styles = useStylesMessage();

  return (
    <div
      className={[
        styles.agentMessage,
        isLastMessage ? styles.lastAgentMessage : "",
      ].join(" ")}
    >
      <TextWithTags text={text} sendEvent={() => {}} shouldTruncate={false} />
    </div>
  );
};

const UserMessageGroup = ({
  messages,
  customWidgetStyle,
  setLastUserUtteranceRef,
}) => {
  const styles = useStylesMessageGroups();
  return (
    <>
      <div
        className={styles.userMessageGroupContainer}
        ref={setLastUserUtteranceRef}
      >
        {pipe(
          map((message) => message.text),
          filter(complement(isEmpty)),
          addIndex(map)((text, index) => (
            <UserUtterance
              key={index}
              text={text}
              isLastMessage={index === messages.length - 1}
              customWidgetStyle={customWidgetStyle}
            />
          ))
        )(messages)}
      </div>
    </>
  );
};

const CustomAvatar = ({ customAvatar, customWidgetStyle }) => (
  <Avatar
    customAvatar={
      customWidgetStyle && customWidgetStyle.customAvatar
        ? customWidgetStyle.customAvatar
        : customAvatar
    }
  />
);

const UserUtterance = ({ text, isLastMessage, customWidgetStyle }) => {
  const styles = useStylesMessage(customWidgetStyle);
  return (
    <div
      className={[
        styles.userMessage,
        isLastMessage ? styles.lastUserMessage : "",
      ].join(" ")}
    >
      <TextWithTags text={text} sendEvent={() => {}} shouldTruncate={false} />
    </div>
  );
};

const BotUtterance = ({
  text,
  isLastMessage,
  shouldTruncate,
  sendEvent,
  customWidgetStyle,
}) => {
  const styles = useStylesMessage(customWidgetStyle);
  return (
    <div
      className={[
        styles.botMessage,
        isLastMessage ? styles.lastMessage : "",
      ].join(" ")}
    >
      <TextWithTags
        text={text}
        sendEvent={sendEvent}
        shouldTruncate={shouldTruncate}
      />
    </div>
  );
};

const isTextualBotMessage = (element) =>
  !element.results && !element.title && element.text;

const hasYesNoSuggestions = (suggestions) =>
  suggestions.length &&
  suggestions.find((x) =>
    ["yes", "no", "i agree", "i disagree"].includes(x.displayText.toLowerCase())
  );

const messageContainsQuestion = (messages) => last(messages).text.includes("?");

const messageContainsAllowMicFirstMessage = (messages) =>
  last(messages).text.includes(
    preRecordedMessages["allowMicFirstMessage"].text
  );

const BotMessageGroup = ({
  messageGroup,
  sendEvent,
  onSuggestionClick,
  customAvatar,
  onResultsClick,
  onMapClick,
  shouldTruncate,
  customWidgetStyle,
  isLastGroup,
  isFeedbackActivated,
  handleFeedback,
  setLastBotUtteranceRef,
  disableShowMoreSuggestions,
}) => {
  const simpleMessagesIndices = messageGroup.messages
    .map((element, i) => [element, i])
    .filter(([element]) => isTextualBotMessage(element))
    .map((_, index) => index);
  const styles = useStylesMessageGroups();

  const resultsWithLocation = messageGroup.results.filter(
    (result) => result.latlng || result.edges.some((edge) => edge.latlng)
  );

  const [feedback, setFeedback] = useState();
  const textualBotMessages = messageGroup.messages.filter(isTextualBotMessage);
  const shouldShowFeedback =
    isFeedbackActivated &&
    // Previous feedbacks are sticky only if user voted.
    (isLastGroup || feedback !== undefined) &&
    ((textualBotMessages.length &&
      // Don't show feedback if thumb/up down may be confused with a question, yes/no suggestions or if it's the "allow mic" message.
      !messageContainsQuestion(textualBotMessages) &&
      !hasYesNoSuggestions(messageGroup.suggestions) &&
      !messageContainsAllowMicFirstMessage(textualBotMessages)) ||
      // Always show feedback if results are returned unless we have the "allow mic" message.
      (messageGroup.results.length > 0 &&
        !messageContainsAllowMicFirstMessage(textualBotMessages)));

  const onFeedback = (feedbackVal) => {
    setFeedback(feedbackVal);
    handleFeedback(
      feedbackVal,
      textualBotMessages[textualBotMessages.length - 1]
    );
  };

  return (
    <div ref={isLastGroup ? setLastBotUtteranceRef : () => {}}>
      {messageGroup.results && messageGroup.results.length > 1 ? (
        <CarouselCards
          sendEvent={sendEvent}
          items={messageGroup.results}
          onSuggestionClick={onSuggestionClick}
          onMapClick={onMapClick}
        />
      ) : null}
      <div
        className={styles.messageGroupContainer}
        style={{ display: "flex", alignItems: "flex-end" }}
      >
        <CustomAvatar
          customAvatar={customAvatar}
          customWidgetStyle={customWidgetStyle}
        />
        <div className={styles.messageGroup}>
          {messageGroup.messages
            .filter((message) => message.title)
            .map((message, index) => (
              <CardResult
                key={index}
                result={message}
                sendEvent={sendEvent}
                onMapClick={onMapClick}
                onSuggestionClick={onSuggestionClick}
              />
            ))}
          {messageGroup.results && messageGroup.results.length > 1 ? (
            <div className={styles.lowerContentWrapper}>
              <Suggestion
                icon={faList}
                displayText="List view"
                handleClick={() => onResultsClick(messageGroup.results)}
              />
              {resultsWithLocation.length ? (
                <Suggestion
                  icon={faMapMarkerAlt}
                  displayText="Show on map"
                  handleClick={() => onMapClick(resultsWithLocation)}
                />
              ) : null}
            </div>
          ) : null}
          {textualBotMessages.map((message, index) => (
            <BotUtterance
              key={index}
              isLastMessage={
                index ===
                simpleMessagesIndices[simpleMessagesIndices.length - 1]
              }
              text={message.text}
              shouldTruncate={shouldTruncate}
              sendEvent={sendEvent}
              customWidgetStyle={customWidgetStyle}
            />
          ))}
        </div>
      </div>
      {messageGroup.suggestions && messageGroup.suggestions.length ? (
        <div className={styles.suggestionContainer}>
          <Suggestions
            suggestions={messageGroup.suggestions}
            handleClick={onSuggestionClick}
            customWidgetStyle={customWidgetStyle}
            disableShowMoreSuggestions={disableShowMoreSuggestions}
          />
        </div>
      ) : null}
      {shouldShowFeedback && (
        <div className={styles.feedbackContainer}>
          <Feedback handleFeedback={onFeedback} />
        </div>
      )}
    </div>
  );
};

const makeMessageGroup = ([firstMessage, ...rest]) => ({
  type: firstMessage.type,
  messages: [firstMessage, ...rest].map(prop("message")),
  results: firstMessage.results || [],
  suggestions: firstMessage.suggestions || [],
});

const mapButLast = curry((fn, c) =>
  unless(
    pipe(tail, isEmpty),
    converge(prepend, [pipe(head, fn), pipe(tail, mapButLast(fn))])
  )(c)
);

const isItChristmas = () => {
  const date = new Date();
  // getMonth() in JavaScript is 0-indexed, while is getDate() is 1-indexed.
  return (
    (date.getMonth() === 11 && date.getDate() >= 14) ||
    (date.getMonth() === 0 && date.getDate() <= 4)
  );
};

const scrollToElement = (element) =>
  // scrollIntoView not available in test mode.
  element && element.scrollIntoView && element.scrollIntoView();

export default ({
  triggerAgentScrollDown,
  isAgent,
  messages,
  isThinking,
  transcribedText,
  numberOfIterationsToShow,
  onSuggestionClick,
  customAvatar,
  onResultsClick,
  onMapClick,
  sendEvent,
  shouldTruncate,
  hyroLogo,
  customWidgetStyle,
  isFeedbackActivated,
  decorateOnHolidays,
  disableShowMoreSuggestions,
}) => {
  const [loadingSpriteRef, setLoadingSpriteRef] = useState();
  const [messageEndRef, setMessageEndRef] = useState();
  const [chatRectangleRef, setRectangleRef] = useState();
  const [lastBotUtteranceRef, setLastBotUtteranceRef] = useState();
  const [lastUserUtteranceRef, setLastUserUtteranceRef] = useState();
  const [lastScrolledToElement, setLastScrolledToElement] = useState();

  useEffect(() => {
    if (isAgent) {
      scrollToElement(messageEndRef);
    }
  }, [isAgent, messageEndRef, triggerAgentScrollDown]);

  useEffect(() => {
    when(
      allPass([
        always(isAgent),
        complement(isNil),
        complement(pipe(length, equals(0))),
        pipe(last, propEq("type", eventTypes.USER_UTTERANCE)),
      ]),
      () => scrollToElement(messageEndRef)
    )(messages);
  }, [isAgent, messageEndRef, messages]);

  useEffect(() => {
    if (!chatRectangleRef || !lastBotUtteranceRef || !lastUserUtteranceRef) {
      return;
    }

    const { height: chatHeight } = chatRectangleRef.getBoundingClientRect();
    const { height: botUtteranceHeight } =
      lastBotUtteranceRef.getBoundingClientRect();
    const { height: userUtteranceHeight } =
      lastUserUtteranceRef.getBoundingClientRect();

    if (botUtteranceHeight + userUtteranceHeight > chatHeight) {
      if (
        lastScrolledToElement &&
        lastScrolledToElement.isSameNode(lastUserUtteranceRef)
      ) {
        // TODO(nur): `Chat` component is re-rendered upon clicks for unknown reason, this makes sure we don't scroll again to the same element.
        return;
      }
      setLastScrolledToElement(lastUserUtteranceRef);
      scrollToElement(lastUserUtteranceRef);
    } else {
      scrollToElement(messageEndRef);
    }
  }, [
    messages,
    messageEndRef,
    chatRectangleRef,
    lastBotUtteranceRef,
    lastScrolledToElement,
    lastUserUtteranceRef,
  ]);

  useEffect(() => {
    scrollToElement(loadingSpriteRef);
  }, [loadingSpriteRef]);

  const useStyles = createUseStyles(
    makeStylesFromThemeAndCustomStyles(
      (theme) => ({
        chatInterface: {
          width: "stretch",
          boxSizing: "border-box !important",
          height: "84%",
          padding: "8px 8px 8px 8px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
          alignItems: "stretch",
          marginBottom: "0",
          backgroundColor: theme.lightWhite,
          border: "0",
          zIndex: "3",
          flexGrow: "1",
          overflowY: "scroll",
          overflowX: "hidden",
          "-ms-overflow-style": "none",
          scrollbarWidth: "none",
          WebkitOverflowScrolling: "touch",
        },
        chatInterfaceContentWrapper: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
        chatInterfaceMessagesWrapper: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "start",
        },
        poweredByHyroWrapper: {
          margin: "10px 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        feedbackThankYouWrapper: {
          position: "absolute",
          bottom: "70px",
          width: "100%",
          margin: "10px 0",
          justifyContent: "center",
          display: "none",
          alignItems: "center",
        },
        feedbackThankYou: {
          background: "rgba(215, 215, 215, 0.9)",
          height: "45px",
          minWidth: "220px",
          borderRadius: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        feedbackThankYouVisible: {
          display: "flex",
          animationName: "$fade-in-out",
          animationDuration: "2s",
        },
        "@keyframes fade-in-out": {
          "0%": { opacity: "0" },
          "25%": { opacity: "1" },
          "75%": { opacity: "1" },
          "100%": { opacity: "0" },
        },
      }),
      customWidgetStyle
    )
  );
  const styles = useStyles();

  const [showFeedbackThankYou, setShowFeedbackThankYou] = useState(false);
  const handleFeedback = (feedbackVal, botMessage) => {
    setShowFeedbackThankYou(true);
    setTimeout(() => {
      setShowFeedbackThankYou(false);
    }, 2 * 1000);
    sendEvent(eventTypes.BOT_UTTERANCE_FEEDBACK, {
      feedback: feedbackVal,
      botMessage,
    });
  };

  const renderMessageGroup = (messageGroup, isLastGroup) => {
    if (messageGroup.type === eventTypes.USER_UTTERANCE)
      return (
        <UserMessageGroup
          messages={messageGroup.messages}
          customWidgetStyle={customWidgetStyle}
          setLastUserUtteranceRef={setLastUserUtteranceRef}
        />
      );

    if (messageGroup.type === eventTypes.AGENT_UTTERANCE) {
      return (
        <AgentMessageGroup
          messages={messageGroup.messages}
          isLastGroup={isLastGroup}
        />
      );
    }

    if (messageGroup.type === eventTypes.BOT_UTTERANCE) {
      return (
        <BotMessageGroup
          {...{
            messageGroup,
            sendEvent,
            onSuggestionClick,
            customAvatar,
            onMapClick,
            onResultsClick,
            shouldTruncate,
            customWidgetStyle,
            isLastGroup,
            isFeedbackActivated,
            handleFeedback,
            setLastBotUtteranceRef,
            disableShowMoreSuggestions,
          }}
        />
      );
    }
  };

  const renderMessages = (messages) => {
    const messageGroups = pipe(
      groupWith(eqProps("type")),
      numberOfIterationsToShow !== -1
        ? takeLast(2 * numberOfIterationsToShow)
        : identity,
      map(makeMessageGroup)
    )(messages);

    return pipe(
      filter(prop("messages")),
      mapButLast(over(lensProp("suggestions"), filter(prop("isSticky")))),
      addIndex(map)((messageGroup, index) =>
        renderMessageGroup(
          messageGroup,
          index !== 0 && index === messageGroups.length - 1
        )
      ),
      addIndex(map)((groupComponent, index) => (
        <div key={`message-group-chat-${index}`}> {groupComponent} </div>
      ))
    )(messageGroups);
  };

  return (
    <div
      className={styles.chatInterface}
      role="log"
      aria-live="polite"
      ref={setRectangleRef}
    >
      {decorateOnHolidays && isItChristmas() ? <ChristmasLights /> : null}
      <div className={styles.chatInterfaceContentWrapper}>
        <div className={styles.chatInterfaceMessagesWrapper}>
          {renderMessages(messages)}
          {isThinking && (
            <div
              className={styles.messageGroupContainer}
              style={{
                display: "flex",
                alignItems: "flex-end",
                marginTop: "8px",
              }}
            >
              <CustomAvatar
                customAvatar={customAvatar}
                customWidgetStyle={customWidgetStyle}
              />
              <div className={styles.messageGroup}>
                <LoadingSprite />
              </div>
              <div ref={setLoadingSpriteRef} />
            </div>
          )}
          {transcribedText && (
            <UserMessageGroup
              messages={[{ text: transcribedText }]}
              styles={styles}
              customWidgetStyle={customWidgetStyle}
            />
          )}
          <div
            style={{ float: "left", clear: "both" }}
            ref={setMessageEndRef}
          />
        </div>
        {hyroLogo && (
          <div className={styles.poweredByHyroWrapper}>
            <PoweredByHyro type={hyroLogo} sendEvent={sendEvent} />
          </div>
        )}
        <div
          className={[
            styles.feedbackThankYouWrapper,
            showFeedbackThankYou ? styles.feedbackThankYouVisible : "",
          ].join(" ")}
        >
          <div className={styles.feedbackThankYou}>
            Thank you for the feedback
          </div>
        </div>{" "}
      </div>
    </div>
  );
};
